import React from 'react';
import { Link } from '@tanstack/react-router';
import {
  Box,
  Button,
  Dialog,
  DialogTrigger,
  DialogFooter,
  Flex,
  Text,
  TextArea,
  Spinner,
  useToast,
} from '@chatsdet/ui';
import { CheckCircle, WarningCircle, Clock } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import {
  useIntendedBehaviorStream,
  type IntendedBehaviorResult,
} from '../../../hooks/useIntendedBehaviorStream';

type NotABugModalProps = {
  issueId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
};

type PatchResponse = {
  ok: boolean;
  status: string;
  statusUpdatedAt: string;
  closedAt: string | null;
  intendedBehaviorJobId: string | null;
  /** Present when `intendedBehaviorJobId` is null — the job couldn't be enqueued. */
  intendedBehaviorError?: string;
};

/** Fixed, user-facing copy — never the raw server/job error string. */
const RULE_RECORDING_FAILED_COPY = "The issue is closed, but the rule couldn't be recorded.";
const RULE_RECORDING_TIMEOUT_COPY =
  'The issue is closed. The rule is still being recorded — check the capability later.';

function ruleActionHeadline(result: IntendedBehaviorResult): string {
  const capabilityLabel = result.capabilityCode
    ? `${result.capabilityCode} · ${result.capabilityTitle}`
    : result.capabilityTitle;
  switch (result.action) {
    case 'added':
      return `Rule added to ${capabilityLabel}`;
    case 'matched':
      return `Matched an existing rule on ${capabilityLabel}`;
    case 'updated':
      return `Rule updated on ${capabilityLabel}`;
    case 'proposed':
      return `Proposed a change to a rule on ${capabilityLabel} — a teammate wrote that rule, so it needs their review`;
    default:
      return `Recorded on ${capabilityLabel}`;
  }
}

/**
 * "Working as intended" close dialog.
 *
 * Records the intended behaviour as a product-map capability rule, then
 * reports where the rule landed. The issue itself closes as `not_a_bug` as
 * soon as the PATCH succeeds — rule recording happens afterward and never
 * reopens or reverts that close, even if it fails.
 */
export function NotABugModal({ issueId, isOpen, onOpenChange, onComplete }: NotABugModalProps) {
  const [intendedBehavior, setIntendedBehavior] = React.useState('');
  const [jobId, setJobId] = React.useState<string | null>(null);
  // Set once the PATCH succeeds — distinct from `jobId` because the job can
  // legitimately fail to enqueue (jobId stays null) while the issue is still
  // closed and the dialog must still leave the form phase.
  const [submitted, setSubmitted] = React.useState(false);
  const [enqueueFailed, setEnqueueFailed] = React.useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const stream = useIntendedBehaviorStream(jobId);
  // Combined outcome: an enqueue failure short-circuits straight to 'failed'
  // without ever having a jobId to stream from.
  const status = enqueueFailed ? 'failed' : stream.status;

  // Log the raw diagnostic detail once per transition into 'failed' — the
  // dialog itself only ever shows the fixed copy below.
  React.useEffect(() => {
    if (stream.status === 'failed' && stream.errorMessage) {
      console.error('[WorkingAsIntended] rule recording failed:', stream.errorMessage);
    }
  }, [stream.status, stream.errorMessage]);

  const invalidateIssueQueries = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['issues'] }).catch(() => {});
    queryClient.invalidateQueries({ queryKey: ['v2-issues'] }).catch(() => {});
    queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
    queryClient.invalidateQueries({ queryKey: ['issue-detail-v2', issueId] }).catch(() => {});
  }, [queryClient, issueId]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/issues/${issueId}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          status: 'not_a_bug',
          intendedBehavior: intendedBehavior.trim(),
        }),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to close the issue');
      }
      return (await res.json()) as PatchResponse;
    },
    onSuccess: (data) => {
      invalidateIssueQueries();
      onComplete?.();
      setSubmitted(true);
      if (data.intendedBehaviorJobId) {
        setJobId(data.intendedBehaviorJobId);
      } else {
        console.error(
          '[WorkingAsIntended] rule recording could not be enqueued:',
          data.intendedBehaviorError ?? '(no detail)'
        );
        setEnqueueFailed(true);
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to close the issue';
      toast.error(message);
    },
  });

  const handleSubmit = () => {
    if (!intendedBehavior.trim()) return;
    mutation.mutate();
  };

  const handleOpenChange = (open: boolean) => {
    // Closing is always safe: the issue is already closed as of `submitted`,
    // and any in-flight recording continues server-side regardless.
    onOpenChange(open);
    if (!open) {
      setIntendedBehavior('');
      setJobId(null);
      setSubmitted(false);
      setEnqueueFailed(false);
    }
  };

  const isFormPhase = !submitted;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange} size="lg">
      <span style={{ display: 'none' }} />
      <Dialog title="Working as intended">
        {isFormPhase ? (
          <Box className="space-y-4">
            <TextArea
              label="Intended behavior"
              description="Describe what the product is supposed to do here. Cofactor records it as a rule so it isn't reported again."
              value={intendedBehavior}
              onChange={setIntendedBehavior}
              rows={5}
              isRequired
              isDisabled={mutation.isPending}
              placeholder="Describe the intended behavior..."
            />
          </Box>
        ) : (
          <Box className="space-y-4">
            {status === 'recording' && (
              <Flex align="center" gap="sm">
                <Spinner size="sm" />
                <Text size="sm" className="text-muted-foreground">
                  Recording the rule...
                </Text>
              </Flex>
            )}
            {status === 'landed' && stream.result && (
              <Flex align="start" gap="sm">
                <CheckCircle size={20} weight="fill" className="text-success shrink-0 mt-0.5" />
                <Box className="space-y-2">
                  <Text size="sm" weight="medium">
                    {ruleActionHeadline(stream.result)}
                  </Text>
                  {stream.result.action === 'proposed' && (
                    <>
                      <Text size="xs" className="text-muted-foreground">
                        Proposed
                      </Text>
                      <Text size="sm">{stream.result.proposedStatement}</Text>
                    </>
                  )}
                  <Text size="xs" className="text-muted-foreground">
                    {stream.result.action === 'proposed' ? 'Current rule (unchanged)' : 'Recorded as'}
                  </Text>
                  <Text size="sm">{stream.result.statement}</Text>
                  <Link
                    to="/lookup/$id"
                    params={{ id: stream.result.capabilityId }}
                    className="text-sm text-primary hover:underline inline-block"
                  >
                    View capability
                  </Link>
                </Box>
              </Flex>
            )}
            {status === 'failed' && (
              <Flex align="start" gap="sm">
                <WarningCircle size={20} weight="fill" className="text-destructive shrink-0 mt-0.5" />
                <Text size="sm">{RULE_RECORDING_FAILED_COPY}</Text>
              </Flex>
            )}
            {status === 'timeout' && (
              <Flex align="start" gap="sm">
                <Clock size={20} weight="fill" className="text-status-waiting shrink-0 mt-0.5" />
                <Text size="sm">{RULE_RECORDING_TIMEOUT_COPY}</Text>
              </Flex>
            )}
          </Box>
        )}

        <DialogFooter>
          <Flex gap="sm">
            {isFormPhase ? (
              <>
                <Button
                  variant="outline"
                  onPress={() => handleOpenChange(false)}
                  isDisabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onPress={handleSubmit}
                  isDisabled={mutation.isPending || !intendedBehavior.trim()}
                >
                  {mutation.isPending ? 'Closing...' : 'Working as intended'}
                </Button>
              </>
            ) : (
              // Enabled even while recording — closing is safe, the issue is
              // already closed, and recording continues server-side.
              <Button variant="primary" onPress={() => handleOpenChange(false)}>
                Done
              </Button>
            )}
          </Flex>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
